import React, { useEffect, useState } from 'react';
import {
  getDepartments, getBranches,
  createDepartment, updateDepartment, deleteDepartment
} from '../api';
import styles from './Page.module.css';

const emptyForm = { name: '', description: '', branchIds: [] };

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchDepartments();
    getBranches().then(r => setBranches(r.data));
  }, []);

  const fetchDepartments = () =>
    getDepartments().then(r => setDepartments(r.data));

  const handleChange = e =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleBranchToggle = (id) => {
    const idStr = id.toString();
    const already = form.branchIds.includes(idStr);
    setForm({
      ...form,
      branchIds: already
        ? form.branchIds.filter(b => b !== idStr)
        : [...form.branchIds, idStr],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.branchIds.length === 0) {
      setMsg('Please select at least one branch.');
      return;
    }
    setLoading(true);
    const payload = {
      name: form.name,
      description: form.description,
      branches: form.branchIds.map(id => ({ id: parseInt(id) })),
    };
    try {
      if (editId) {
        await updateDepartment(editId, payload);
        setMsg('Department updated successfully!');
      } else {
        await createDepartment(payload);
        setMsg('Department added successfully!');
      }
      setForm(emptyForm);
      setEditId(null);
      fetchDepartments();
      setTimeout(() => setMsg(''), 3000);
    } catch {
      setMsg('Operation failed.');
    }
    setLoading(false);
  };

  const handleEdit = (d) => {
    setEditId(d.id);
    setForm({
      name: d.name || '',
      description: d.description || '',
      branchIds: d.branches?.map(b => b.id.toString()) || [],
    });
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await deleteDepartment(id);
      fetchDepartments();
    } catch {
      alert('Failed to delete. Department may have linked doctors.');
    }
  };

  return (
    <div>
      <h2 className={styles.heading}>🏬 Manage Departments</h2>
      <div className={styles.grid}>
        <div className={styles.card}>
          <h3>{editId ? 'Edit Department' : 'Add New Department'}</h3>
          <form onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label>Department Name *</label>
              <input name="name" value={form.name}
                onChange={handleChange}
                placeholder="e.g. Cardiology" required />
            </div>
            <div className={styles.field}>
              <label>Description</label>
              <input name="description" value={form.description}
                onChange={handleChange}
                placeholder="e.g. Heart and cardiovascular care" />
            </div>
            <div className={styles.field}>
              <label>Assign to Branches * (select one or more)</label>
              <div className={styles.checkboxGrid}>
                {branches.map(b => (
                  <label key={b.id} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={form.branchIds.includes(b.id.toString())}
                      onChange={() => handleBranchToggle(b.id)}
                    />
                    <span>{b.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {msg && <p className={styles.msg}>{msg}</p>}
            <div className={styles.btnRow}>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Saving...' : editId ? 'Update Department' : 'Add Department'}
              </button>
              {editId && (
                <button type="button" className={styles.btnOutline}
                  onClick={() => { setEditId(null); setForm(emptyForm); }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className={styles.card}>
          <h3>All Departments ({departments.length})</h3>
          <div className={styles.list}>
            {departments.map(d => (
              <div key={d.id} className={styles.item}>
                <div>
                  <strong>{d.name}</strong>
                  <span>{d.description || 'No description'}</span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {d.branches?.map(b => (
                      <span key={b.id} className={styles.tag}>{b.name}</span>
                    ))}
                  </div>
                </div>
                <div className={styles.actions}>
                  <button className={styles.editBtn}
                    onClick={() => handleEdit(d)}>✏️ Edit</button>
                  <button className={styles.deleteBtn}
                    onClick={() => handleDelete(d.id)}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
